import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { environment } from '../../config/environment';
import { apiRequest } from '../../services/apiClient';
import createSidebarSections from '../../config/sidebarSections';
import DashboardLayout from '../../components/DashboardLayout';
import '../../pages/WelcomePage.css';
import '../../pages/LoginPage.css';
import './create.css';

const JOB_STATUS = {
  ACTIVE: '1',
  INACTIVE: '2',
  DELETED: '9',
};

const SALARY_FREQUENCY = [
  { value: '1', label: 'Hourly' },
  { value: '2', label: 'Daily' },
  { value: '3', label: 'Weekly' },
  { value: '4', label: 'Monthly' },
  { value: '5', label: 'Yearly' },
];

const CreateJobOpening = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [skillInput, setSkillInput] = useState('');
  const [isLoadingSequence, setIsLoadingSequence] = useState(true);
  const [showRecruiterFields, setShowRecruiterFields] = useState(true);
  
  const [jobTypes, setJobTypes] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [educationTypes, setEducationTypes] = useState([]);
  const [isLoadingMasters, setIsLoadingMasters] = useState(true);

  const [formData, setFormData] = useState({
title: '',
    job_type: '',
    designation: '',
    education: '',
    description: '',
    skills: [],
    experience: '',
    openings: '',
    salary_min: '',
    salary_max: '',
    salary_frequency: '4',
    location: '',
    deadline_date_time: '',
    recruiter_name: '',
    recruiter_contact: '',
    recruiter_email: '',
    status: JOB_STATUS.ACTIVE,
    sequence: '',
  });

  const userName = useMemo(() => {
    const userStr = localStorage.getItem('authUser') || sessionStorage.getItem('authUser');
    if (!userStr) return 'Rakesh';
    try {
      const user = JSON.parse(userStr);
      return user?.name || user?.fullName || user?.email || 'Rakesh';
    } catch {
      return 'Rakesh';
    }
  }, []);

  const calculateNextSequence = useCallback((jobList) => {
    if (!Array.isArray(jobList) || jobList.length === 0) {
      return '1';
    }
    const sequences = jobList
      .map((item) => Number(item?.sequence))
      .filter((seq) => !isNaN(seq));
    if (sequences.length === 0) {
      return '1';
    }
    const maxSequence = Math.max(...sequences);
    return String(maxSequence + 1);
  }, []);

  const fetchNextSequence = useCallback(async () => {
    setIsLoadingSequence(true);
    try {
      const { ok, data } = await apiRequest(environment.endpoints.jobApplicationGetAll, {
        method: 'POST',
      });

      if (!ok || data?.response !== true) {
        setFormData((prev) => ({ ...prev, sequence: '1' }));
        return;
      }

      const jobList = Array.isArray(data?.data) ? data.data : [];
      const nextSeq = calculateNextSequence(jobList);
      setFormData((prev) => ({ ...prev, sequence: nextSeq }));
    } catch (error) {
      setFormData((prev) => ({ ...prev, sequence: '1' }));
    } finally {
      setIsLoadingSequence(false);
    }
  }, [calculateNextSequence]);

  const fetchMasterData = useCallback(async () => {
    setIsLoadingMasters(true);
    try {
      const [jobTypesRes, designationsRes, educationTypesRes] = await Promise.all([
        apiRequest(environment.endpoints.masterJobTypeGetAll, { method: 'POST' }),
        apiRequest(environment.endpoints.masterPostsGetAll, { method: 'POST' }),
        apiRequest(environment.endpoints.masterEducationTypeGetAll, { method: 'POST' }),
      ]);

      if (jobTypesRes?.ok && jobTypesRes?.data?.response === true) {
        const jobTypesList = Array.isArray(jobTypesRes.data.data) ? jobTypesRes.data.data : [];
        setJobTypes(jobTypesList.map(item => ({ id: item.id, name: item.name })));
      } else {
        setJobTypes([]);
      }

      if (designationsRes?.ok && designationsRes?.data?.response === true) {
        const designationsList = Array.isArray(designationsRes.data.data) ? designationsRes.data.data : [];
        setDesignations(designationsList.map(item => ({ id: item.id, name: item.name })));
      } else {
        setDesignations([]);
      }

      if (educationTypesRes?.ok && educationTypesRes?.data?.response === true) {
        const educationList = Array.isArray(educationTypesRes.data.data) ? educationTypesRes.data.data : [];
        setEducationTypes(educationList.map(item => ({ id: item.id, name: item.name })));
      } else {
        setEducationTypes([]);
      }
    } catch (error) {
      setJobTypes([]);
      setDesignations([]);
      setEducationTypes([]);
    } finally {
      setIsLoadingMasters(false);
    }
  }, []);

  useEffect(() => {
    fetchNextSequence();
    fetchMasterData();
  }, [fetchNextSequence, fetchMasterData]);

  const renderLogoLoader = () => (
    <div className="mini-logo-loader" role="status" aria-live="polite" aria-label="Loading">
      <span className="loader-orbit loader-orbit-one"></span>
      <span className="loader-orbit loader-orbit-two"></span>
      <span className="loader-orbit loader-orbit-three"></span>
      <img
        src={process.env.PUBLIC_URL + '/images/logo.png'}
        alt="Company Logo"
        className="session-loader-logo"
      />
    </div>
  );

  const getCurrentLocationOrNull = () => new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve({ latitude: null, longitude: null });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      () => {
        resolve({ latitude: null, longitude: null });
      },
      {
        enableHighAccuracy: true,
        timeout: 8000,
        maximumAge: 0,
      }
    );
  });

  const validateField = (name, value) => {
    switch (name) {
      case 'title':
        if (!value.trim()) return 'Job title is required.';
        return '';
      
      case 'job_type':
        return '';
      
      case 'designation':
        return '';
      
      case 'education':
        return '';
      
      case 'experience':
        return '';
      
      case 'openings':
        if (value && (isNaN(Number(value)) || Number(value) < 1)) return 'Openings must be a positive number.';
        return '';
      
      case 'salary_min':
        if (value && (isNaN(Number(value)) || Number(value) < 0)) return 'Minimum salary must be 0 or greater.';
        return '';
      
      case 'salary_max':
        if (value && (isNaN(Number(value)) || Number(value) < 0)) return 'Maximum salary must be 0 or greater.';
        if (value && formData.salary_min && Number(value) < Number(formData.salary_min)) return 'Max salary must be greater than or equal to min salary.';
        return '';
      
      case 'location':
        return '';
      
      case 'deadline_date_time':
        return '';
      
      case 'recruiter_name':
        if (showRecruiterFields && value.trim() && value.trim().length < 2) return 'Name must be at least 2 characters.';
        return '';
      
      case 'recruiter_contact':
        if (showRecruiterFields && value.trim() && !/^\d{10}$/.test(value.trim())) return 'Contact must be exactly 10 digits.';
        return '';
      
      case 'recruiter_email':
        if (showRecruiterFields && value.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) return 'Please enter a valid email address.';
        return '';
      
      case 'sequence':
        if (!value || isNaN(Number(value)) || Number(value) <= 0) return 'Sequence must be a positive number.';
        return '';
      
      default:
        return '';
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    let finalValue = value;
    
    if (name === 'recruiter_contact') {
      const cleaned = value.replace(/\D/g, '');
      if (cleaned.length <= 10) {
        finalValue = cleaned;
      } else {
        return;
      }
    }
    
    setFormData((prev) => {
      const newFormData = { ...prev, [name]: finalValue };
      
      const error = validateField(name, finalValue);
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        [name]: error,
      }));
      
      if (name === 'salary_min' && prev.salary_max) {
        const maxError = validateField('salary_max', prev.salary_max);
        setFormErrors((prevErrors) => ({
          ...prevErrors,
          salary_max: maxError,
        }));
      }
      
      return newFormData;
    });
  };

  const handleRecruiterToggle = (e) => {
    const checked = e.target.checked;
    setShowRecruiterFields(checked);
    if (!checked) {
      setFormData((prev) => ({
        ...prev,
        recruiter_name: '',
        recruiter_contact: '',
        recruiter_email: '',
      }));
      setFormErrors((prev) => ({
        ...prev,
        recruiter_name: '',
        recruiter_contact: '',
        recruiter_email: '',
      }));
    }
  };

  const handleAddSkill = () => {
    const trimmedSkill = skillInput.trim();
    if (!trimmedSkill) return;
    if (formData.skills.includes(trimmedSkill)) {
      setFormErrors((prev) => ({ ...prev, skills: 'This skill is already added.' }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      skills: [...prev.skills, trimmedSkill],
    }));
    setSkillInput('');
    setFormErrors((prev) => ({ ...prev, skills: '' }));
  };

  const handleRemoveSkill = (skillToRemove) => {
    setFormData((prev) => ({
      ...prev,
      skills: prev.skills.filter((skill) => skill !== skillToRemove),
    }));
  };

  const handleSkillKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddSkill();
    }
  };

  const validateForm = () => {
    const errors = {};
    
    const titleError = validateField('title', formData.title);
    if (titleError) errors.title = titleError;
    
    const openingsError = validateField('openings', formData.openings);
    if (openingsError) errors.openings = openingsError;
    
    const salaryMinError = validateField('salary_min', formData.salary_min);
    if (salaryMinError) errors.salary_min = salaryMinError;
    
    const salaryMaxError = validateField('salary_max', formData.salary_max);
    if (salaryMaxError) errors.salary_max = salaryMaxError;
    
    const recruiterNameError = validateField('recruiter_name', formData.recruiter_name);
    if (recruiterNameError) errors.recruiter_name = recruiterNameError;
    
    const recruiterContactError = validateField('recruiter_contact', formData.recruiter_contact);
    if (recruiterContactError) errors.recruiter_contact = recruiterContactError;
    
    const recruiterEmailError = validateField('recruiter_email', formData.recruiter_email);
    if (recruiterEmailError) errors.recruiter_email = recruiterEmailError;
    
    const sequenceError = validateField('sequence', formData.sequence);
    if (sequenceError) errors.sequence = sequenceError;

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const coords = await getCurrentLocationOrNull();
      const payload = {
        title: formData.title.trim(),
        job_type: formData.job_type,
        designation: formData.designation,
        education: formData.education,
description: formData.description.trim(),
        skills: formData.skills,
        experience: formData.experience.trim(),
        openings: formData.openings ? Number(formData.openings) : null,
        salary_min: formData.salary_min ? Number(formData.salary_min) : null,
        salary_max: formData.salary_max ? Number(formData.salary_max) : null,
        salary_frequency: formData.salary_frequency,
        location: formData.location.trim(),
        deadline_date_time: formData.deadline_date_time || null,
        recruiter_name: showRecruiterFields ? formData.recruiter_name.trim() : '',
        recruiter_contact: showRecruiterFields ? formData.recruiter_contact.trim() : '',
        recruiter_email: showRecruiterFields ? formData.recruiter_email.trim() : '',
        status: formData.status,
        sequence: Number(formData.sequence),
        latitude: coords.latitude,
        longitude: coords.longitude,
      };

      const { ok, data } = await apiRequest(environment.endpoints.jobApplicationCreate, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      setIsSubmitting(false);

      if (!ok || data?.response !== true) {
        await Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: data?.message || 'Unable to create job opening.',
          confirmButtonColor: '#c7374e',
        });
        return;
      }

      await Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: data?.message || 'Job opening created successfully.',
        confirmButtonColor: '#0f7a8f',
      });
      navigate('/job-openings');
    } catch (error) {
      setIsSubmitting(false);
      await Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: error?.message || 'Unable to create job opening.',
        confirmButtonColor: '#c7374e',
      });
    }
  };

  const handleLogout = async () => {
    try {
      await apiRequest(environment.endpoints.logout, { method: 'POST' }, { skipValidation: true });
    } catch {
    } finally {
      localStorage.removeItem('authUser');
      sessionStorage.removeItem('authUser');
      localStorage.setItem('logoutRequested', 'true');
      sessionStorage.setItem('logoutRequested', 'true');
      navigate('/login', { replace: true });
    }
  };

  const sections = useMemo(() => createSidebarSections(navigate, 'job-openings'), [navigate]);

  return (
    <DashboardLayout
      displayName={userName}
      pageTitle="Create Job Opening"
      dashboard={{ active: false, onClick: () => navigate('/welcome') }}
      sections={sections}
      onLogout={handleLogout}
    >
      <div className="job-form-wrapper">
        <form className="job-form" onSubmit={handleSubmit} noValidate>
          
          <div className="job-form-section">
            <h3 className="job-section-heading">Basic Information</h3>
            <div className="job-field-row">
              <div className="job-field-group">
                <label htmlFor="title">Job Title <span className="job-required">*</span></label>
                <input id="title" name="title" type="text" value={formData.title} onChange={handleInputChange} placeholder="Enter job title" disabled={isSubmitting} />
                {formErrors.title && <p className="job-field-error">{formErrors.title}</p>}
              </div>
              <div className="job-field-group">
                <label htmlFor="job_type">Job Type</label>
                <select id="job_type" name="job_type" value={formData.job_type} onChange={handleInputChange} disabled={isSubmitting || isLoadingMasters}>
                  <option value="">{isLoadingMasters ? 'Loading...' : 'Select Job Type'}</option>
                  {jobTypes.map((type) => <option key={type.id} value={type.id}>{type.name}</option>)}
                </select>
                {formErrors.job_type && <p className="job-field-error">{formErrors.job_type}</p>}
              </div>
            </div>

            <div className="job-field-row">
              <div className="job-field-group">
                <label htmlFor="designation">Designation</label>
                <select id="designation" name="designation" value={formData.designation} onChange={handleInputChange} disabled={isSubmitting || isLoadingMasters}>
                  <option value="">{isLoadingMasters ? 'Loading...' : 'Select Designation'}</option>
                  {designations.map((des) => <option key={des.id} value={des.id}>{des.name}</option>)}
                </select>
                {formErrors.designation && <p className="job-field-error">{formErrors.designation}</p>}
              </div>
              <div className="job-field-group">
                <label htmlFor="education">Education Required</label>
                <select id="education" name="education" value={formData.education} onChange={handleInputChange} disabled={isSubmitting || isLoadingMasters}>
                  <option value="">{isLoadingMasters ? 'Loading...' : 'Select Education'}</option>
                  {educationTypes.map((edu) => <option key={edu.id} value={edu.id}>{edu.name}</option>)}
                </select>
                {formErrors.education && <p className="job-field-error">{formErrors.education}</p>}
              </div>
            </div>

<div className="job-field-row">
              <div className="job-field-group">
                <label htmlFor="experience">Experience Required</label>
                <input id="experience" name="experience" type="text" value={formData.experience} onChange={handleInputChange} placeholder="e.g., 3-5 Years" disabled={isSubmitting} />
                {formErrors.experience && <p className="job-field-error">{formErrors.experience}</p>}
              </div>
              <div className="job-field-group">
                <label htmlFor="openings">Number of Openings</label>
                <input id="openings" name="openings" type="number" value={formData.openings} onChange={handleInputChange} placeholder="Enter number of openings" disabled={isSubmitting} min="0" />
                {formErrors.openings && <p className="job-field-error">{formErrors.openings}</p>}
              </div>
            </div>

            <div className="job-field-row">
              <div className="job-field-group" style={{ gridColumn: '1 / -1' }}>
                <label htmlFor="description">Job Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Describe the role, responsibilities, and any other relevant details about this position..."
                  disabled={isSubmitting}
                  rows={5}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', fontFamily: 'inherit', resize: 'vertical' }}
                />
                {formErrors.description && <p className="job-field-error">{formErrors.description}</p>}
              </div>
            </div>
          </div>

          <div className="job-form-section">
            <h3 className="job-section-heading">Required Skills</h3>
            <div className="job-skills-area">
              <div className="job-skills-input-row">
                <input type="text" value={skillInput} onChange={(e) => setSkillInput(e.target.value)} onKeyPress={handleSkillKeyPress} placeholder="Type a skill and press Enter or click Add" disabled={isSubmitting} className="job-skill-input" />
                <button type="button" onClick={handleAddSkill} disabled={isSubmitting} className="job-skill-add-btn">+ Add</button>
              </div>
              {formErrors.skills && <p className="job-field-error">{formErrors.skills}</p>}
              <div className="job-skills-tags">
                {formData.skills.length === 0 && <p className="job-no-skills">No skills added yet</p>}
                {formData.skills.map((skill, index) => (
                  <span key={index} className="job-skill-tag">
                    {skill}
                    <button type="button" onClick={() => handleRemoveSkill(skill)} disabled={isSubmitting} className="job-skill-remove">×</button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="job-form-section">
            <h3 className="job-section-heading">Salary Details</h3>
            <div className="job-field-row">
              <div className="job-field-group">
                <label htmlFor="salary_min">Minimum Salary</label>
                <input id="salary_min" name="salary_min" type="number" value={formData.salary_min} onChange={handleInputChange} placeholder="Enter minimum salary" disabled={isSubmitting} min="0" />
                {formErrors.salary_min && <p className="job-field-error">{formErrors.salary_min}</p>}
              </div>
              <div className="job-field-group">
                <label htmlFor="salary_max">Maximum Salary</label>
                <input id="salary_max" name="salary_max" type="number" value={formData.salary_max} onChange={handleInputChange} placeholder="Enter maximum salary" disabled={isSubmitting} min="0" />
                {formErrors.salary_max && <p className="job-field-error">{formErrors.salary_max}</p>}
              </div>
            </div>
            <div className="job-field-row">
              <div className="job-field-group">
                <label htmlFor="salary_frequency">Salary Frequency</label>
                <select id="salary_frequency" name="salary_frequency" value={formData.salary_frequency} onChange={handleInputChange} disabled={isSubmitting}>
                  {SALARY_FREQUENCY.map((freq) => <option key={freq.value} value={freq.value}>{freq.label}</option>)}
                </select>
              </div>
              <div className="job-field-group"></div>
            </div>
          </div>

          <div className="job-form-section">
            <h3 className="job-section-heading">Location & Deadline</h3>
            <div className="job-field-row">
              <div className="job-field-group">
                <label htmlFor="location">Location</label>
                <input id="location" name="location" type="text" value={formData.location} onChange={handleInputChange} placeholder="e.g., Delhi, India" disabled={isSubmitting} />
                {formErrors.location && <p className="job-field-error">{formErrors.location}</p>}
              </div>
              <div className="job-field-group">
                <label htmlFor="deadline_date_time">Application Deadline</label>
                <input id="deadline_date_time" name="deadline_date_time" type="datetime-local" value={formData.deadline_date_time} onChange={handleInputChange} disabled={isSubmitting} />
                {formErrors.deadline_date_time && <p className="job-field-error">{formErrors.deadline_date_time}</p>}
              </div>
            </div>
          </div>

          <div className="job-form-section">
            <div className="job-section-heading-row">
              <h3 className="job-section-heading">Recruiter Information</h3>
              <label className="recruiter-toggle-label">
                <input 
                  type="checkbox" 
                  checked={showRecruiterFields} 
                  onChange={handleRecruiterToggle}
                  className="recruiter-toggle-checkbox"
                />
                <span className="recruiter-toggle-text">Add recruiter details</span>
              </label>
            </div>
            
            {showRecruiterFields && (
              <>
                <div className="job-field-row">
                  <div className="job-field-group">
                    <label htmlFor="recruiter_name">Recruiter Name</label>
                    <input id="recruiter_name" name="recruiter_name" type="text" value={formData.recruiter_name} onChange={handleInputChange} placeholder="Enter recruiter name" disabled={isSubmitting} />
                    {formErrors.recruiter_name && <p className="job-field-error">{formErrors.recruiter_name}</p>}
                  </div>
                  <div className="job-field-group">
                    <label htmlFor="recruiter_contact">Contact Number</label>
                    <input id="recruiter_contact" name="recruiter_contact" type="tel" value={formData.recruiter_contact} onChange={handleInputChange} placeholder="e.g., 9876543210" disabled={isSubmitting} maxLength="10" />
                    {formErrors.recruiter_contact && <p className="job-field-error">{formErrors.recruiter_contact}</p>}
                  </div>
                </div>
                <div className="job-field-row">
                  <div className="job-field-group">
                    <label htmlFor="recruiter_email">Email Address</label>
                    <input id="recruiter_email" name="recruiter_email" type="email" value={formData.recruiter_email} onChange={handleInputChange} placeholder="e.g., recruiter@example.com" disabled={isSubmitting} />
                    {formErrors.recruiter_email && <p className="job-field-error">{formErrors.recruiter_email}</p>}
                  </div>
                  <div className="job-field-group"></div>
                </div>
              </>
            )}
          </div>

          {/* <div className="job-form-section"> */}
            <h3 className="job-section-heading">Status & Sequence</h3>
            <div className="job-field-row">
              <div className="job-field-group">
                <label htmlFor="status">Job Status</label>
                <select id="status" name="status" value={formData.status} onChange={handleInputChange} disabled={isSubmitting}>
                  <option value={JOB_STATUS.ACTIVE}>Active</option>
                  <option value={JOB_STATUS.INACTIVE}>Inactive</option>
                </select>
              </div>
              <div className="job-field-group">
                <label htmlFor="sequence">
                  Sequence <span className="job-required">*</span>
                  {isLoadingSequence && <span className="sequence-loading"> (Auto-filling...)</span>}
                </label>
                <input 
                  id="sequence" 
                  name="sequence" 
                  type="number" 
                  value={formData.sequence} 
                  onChange={handleInputChange} 
                  placeholder={isLoadingSequence ? "Loading..." : "Auto-generated sequence"} 
                  disabled={isSubmitting || isLoadingSequence} 
                  min="1" 
                  className={isLoadingSequence ? 'input-loading' : ''}
                />
                {formErrors.sequence && <p className="job-field-error">{formErrors.sequence}</p>}
              </div>
            </div>
          {/* </div> */}

          <div className="job-form-actions">
            <button type="button" className="job-btn-cancel" onClick={() => navigate('/job-openings')} disabled={isSubmitting}>Cancel</button>
            <button type="submit" className="job-btn-submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Job Opening'}
            </button>
          </div>
        </form>
      </div>

      {isSubmitting && (
        <div className="api-loader-overlay">
          {renderLogoLoader()}
        </div>
      )}
    </DashboardLayout>
  );
};

export default CreateJobOpening;